import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import API from "@/lib/api";
import { ChevronLeft, Package, QrCode, Tag, Loader2, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toTitleCase } from "@/lib/utils";

export default function ProductDetail() {
  const { code } = useParams();
  const navigate = useNavigate();
  const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await API.get(`/products/${code}`);
        setProduct(data);
      } catch {} finally { setLoading(false); }
    })();
  }, [code]);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#008A99]" /></div>;
  if (!product) return <div className="text-center py-20"><p className="text-gray-400">Product not found</p><button onClick={() => navigate("/app/products")} className="mt-3 text-sm text-[#008A99] font-medium">Back to Products</button></div>;

  const pts = product.mrp ? Math.floor(product.mrp * (product.reagent_type === "open" ? 0.02 : 0.01)) : 0;

  return (
    <div className="space-y-5 animate-slide-up" data-testid="product-detail">
      <button onClick={() => navigate("/app/products")} className="flex items-center gap-1 text-sm text-gray-500 hover:text-[#008A99] font-medium" data-testid="back-to-products"><ChevronLeft className="w-4 h-4" /> Products</button>

      {/* Hero Image */}
      <div className="h-48 bg-gradient-to-br from-[#E0F4F5] to-gray-50 rounded-2xl overflow-hidden flex items-center justify-center relative">
        {product.image_url ? (
          <img src={product.image_url.startsWith("http") ? product.image_url : `${BACKEND}${product.image_url}`} alt={product.name} className="w-full h-full object-contain p-4" />
        ) : (
          <Package className="w-16 h-16 text-[#008A99]/15" />
        )}
      </div>

      {/* Info */}
      <div>
        <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">{product.product_code}</p>
        <h1 className="text-xl font-black text-gray-900 tracking-tight mt-1" style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}>{toTitleCase(product.name)}</h1>
        <p className="text-xs text-gray-500 mt-1">{toTitleCase(product.category)}{product.sub_category ? ` / ${toTitleCase(product.sub_category)}` : ''}</p>
      </div>

      {/* Price & Points Card — only show if MRP is configured */}
      {product.mrp > 0 && (
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4 flex items-center justify-between">
          <div>
            <p className="text-2xl font-black text-gray-900" style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}>Rs. {product.mrp?.toLocaleString()}</p>
            <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">MRP</p>
          </div>
          <div className="text-right bg-[#E0F4F5] rounded-xl px-4 py-2.5">
            <p className="text-lg font-black text-[#008A99]">+{pts} pts</p>
            <p className="text-[9px] text-[#008A99]/60 font-bold uppercase tracking-wider">On scan</p>
          </div>
        </div>
      )}

      {/* Details */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 space-y-4">
        <h3 className="text-xs font-bold text-gray-500 uppercase tracking-[0.15em]">Product Details</h3>
        {product.description ? (
          <p className="text-sm text-gray-600 leading-relaxed">{product.description}</p>
        ) : (
          <p className="text-sm text-gray-400 italic">No description available</p>
        )}
        <div className="grid grid-cols-2 gap-3">
          {product.pack_size && <div className="bg-gray-50 rounded-xl p-3"><p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Pack Size</p><p className="text-sm font-bold text-gray-900 mt-0.5">{product.pack_size}</p></div>}
          <div className="bg-gray-50 rounded-xl p-3"><p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Category</p><p className="text-sm font-bold text-gray-900 mt-0.5">{toTitleCase(product.category)}</p></div>
          {product.mrp > 0 && <div className="bg-gray-50 rounded-xl p-3"><p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Points %</p><p className="text-sm font-bold text-gray-900 mt-0.5">{product.reagent_type === "open" ? "2%" : "1%"} of MRP</p></div>}
        </div>
      </div>

      <Button onClick={() => navigate("/app/scan")} className="w-full h-12 rounded-xl bg-[#008A99] hover:bg-[#006670] text-white font-bold shadow-lg shadow-[#008A99]/20" data-testid="scan-this-product">
        <QrCode className="w-4 h-4 mr-2" /> Scan This Product
      </Button>
    </div>
  );
}
